import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { type VariantProps, cva } from "class-variance-authority"
import { cn } from "@holigral/shared/ui/lib/utils"

// The foundation's three buttons (ADR-0020):
//
//   primary   the sun, with ink on it. One per view.
//   outline   the Khepri outline.
//   ghost     a link that looks like one.
//
// No `default`: `variant` is required, so nobody gets the sun by not choosing.
// No `destructive` until the foundation draws one (docs/dev/frontend.md): red
// is form validation only, so a destructive action asks for `ghost` and the
// confirm dialog carries the weight. The sun is never text, so no variant
// resolves `text-primary`.
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding font-sans text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // A filled sun with no visible border. The border is there and
        // transparent, so all three rungs stand at the same height.
        //
        // Hover darkens rather than fades: the sun over paper at 80% opacity
        // goes chalky, which reads as disabled instead of pressed.
        primary:
          "border-transparent bg-primary text-primary-foreground hover:brightness-95",
        outline:
          "border-khepri-text bg-transparent text-khepri-text hover:bg-khepri-tint aria-expanded:bg-khepri-tint",
        ghost:
          "border-transparent bg-transparent text-khepri-text underline-offset-[3px] hover:underline aria-expanded:bg-khepri-tint",
      },
      size: {
        // 18px of horizontal padding, as drawn.
        default:
          "h-10 gap-1.5 px-4.5 has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        lg: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    // The ghost keeps 4px of horizontal padding at every text size, so it sits
    // in a line of prose like the link it is meant to look like. It has to be
    // a compound variant rather than part of the variant itself: cva emits
    // variant classes before size classes, so a `px-1` up there would be
    // overruled by whichever size the caller asked for.
    compoundVariants: [
      {
        variant: "ghost",
        size: ["default", "xs", "sm", "lg"],
        class:
          "px-1 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1",
      },
    ],
    defaultVariants: {
      size: "default",
    },
  },
)

/** The three the foundation draws. There is no fourth, and no default. */
export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>

function Button({
  className,
  variant,
  size = "default",
  ...props
}: ButtonPrimitive.Props &
  Omit<VariantProps<typeof buttonVariants>, "variant"> & {
    readonly variant: ButtonVariant
  }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
